// /w/:wid/mail/new — a new letter: recipients as chips (up to ten; one mail/send per name), RECENT correspondents,
// subject, the letter with its counter, DISCARD / SEND. The draft is kept on this device until it is sent or discarded.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WorldPage, useWorldCity } from "@/app/WorldPage";
import { StickyBar } from "@/kit/city";
import { IconClose, IconPlus } from "@/kit/icons";
import { TitleBar } from "@/kit/chrome";
import { Confirm } from "@/kit/overlays";
import { Button, Card, CardHead, Medallion, cx } from "@/kit/primitives";
import { useSession } from "@/state/session";
import { usePrompts } from "@/state/prompts";
import { useMail, useMailMutations } from "@/state/socialQueries";
import { GamePromptError } from "@/api/http";
import { fmtNum, plural } from "@/game/format";
import { MAIL_MAX_BODY, MAIL_MAX_RECIPIENTS, MAIL_MAX_SUBJECT, mailItems, recentCorrespondents, splitRecipients } from "@/game/mail";
import "../social.css";

interface Draft {
  to: string[];
  subject: string;
  body: string;
  savedAt: number;
}

const draftKey = (wid: number) => `valor-web:mail-draft:${wid}`;
const loadDraft = (wid: number): Draft | null => {
  try {
    const raw = localStorage.getItem(draftKey(wid));
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
};

export function MailCompose() {
  return (
    <WorldPage>
      <Compose />
    </WorldPage>
  );
}

export function Compose() {
  const { wid, base } = useWorldCity();
  const nav = useNavigate();
  const session = useSession();
  const [params] = useSearchParams();
  const list = useMail(wid);
  const { send } = useMailMutations(wid);
  const draft = useMemo(() => loadDraft(wid), [wid]);
  const [to, setTo] = useState<string[]>(() => (params.get("to") ? splitRecipients(params.get("to")!) : draft?.to ?? []));
  const [typing, setTyping] = useState("");
  const [subject, setSubject] = useState(() => params.get("subject") ?? draft?.subject ?? "");
  const [body, setBody] = useState(() => (params.get("to") || params.get("subject") ? "" : draft?.body ?? ""));
  const [bad, setBad] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(draft && !params.get("to") ? draft.savedAt : null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const toInput = useRef<HTMLInputElement>(null);

  const all = useMemo(() => mailItems(list.data, session.uid), [list.data, session.uid]);
  const recent = useMemo(() => recentCorrespondents(all, 8).filter((n) => !to.some((t) => t.toLowerCase() === n.toLowerCase())), [all, to]);

  // the draft, saved a moment after each change
  useEffect(() => {
    if (!to.length && !subject && !body) { try { localStorage.removeItem(draftKey(wid)); } catch { /* fine */ } setSavedAt(null); return; }
    const t = window.setTimeout(() => {
      const at = Date.now();
      try { localStorage.setItem(draftKey(wid), JSON.stringify({ to, subject, body, savedAt: at } satisfies Draft)); setSavedAt(at); } catch { /* storage may be unavailable */ }
    }, 600);
    return () => window.clearTimeout(t);
  }, [to, subject, body, wid]);

  const addNames = (raw: string) => {
    const names = splitRecipients(raw).filter((n) => n.toLowerCase() !== (session.login ?? "").toLowerCase() || to.length === 0);
    if (!names.length) return;
    setTo((cur) => {
      const next = [...cur];
      for (const n of names) if (!next.some((x) => x.toLowerCase() === n.toLowerCase()) && next.length < MAIL_MAX_RECIPIENTS) next.push(n);
      return next;
    });
    setTyping("");
  };
  const removeName = (n: string) => { setTo((cur) => cur.filter((x) => x !== n)); setBad((s) => { const c = new Set(s); c.delete(n); return c; }); };

  const canSend = to.length > 0 && subject.trim().length > 0 && body.trim().length > 0 && body.length <= MAIL_MAX_BODY && !sending;

  const doSend = async () => {
    const names = typing.trim() ? [...to, ...splitRecipients(typing)].slice(0, MAIL_MAX_RECIPIENTS) : to;
    if (typing.trim()) { setTo(names); setTyping(""); }
    setSending(true);
    const failed = new Set<string>();
    let sent = 0;
    for (const n of names) {
      try {
        await send.mutateAsync({ to: n, subject: subject.trim(), body: body.trim() });
        sent++;
      } catch (e) {
        failed.add(n);
        if (!(e instanceof GamePromptError)) usePrompts.getState().error(e);
      }
    }
    setSending(false);
    setBad(failed);
    if (failed.size) {
      usePrompts.getState().toast(failed.size === 1 ? `${[...failed][0]} wasn't found — check the name.` : `${plural(failed.size, "name")} weren't found: ${[...failed].join(", ")}.`, "error");
      if (sent) usePrompts.getState().toast(`Sent to ${plural(sent, "player")}.`, "success");
      setTo([...failed]);
      return;
    }
    try { localStorage.removeItem(draftKey(wid)); } catch { /* fine */ }
    usePrompts.getState().toast(sent === 1 ? `Letter sent to ${names[0]}.` : `Letter sent to ${plural(sent, "player")}.`, "success");
    nav(`${base}/mail?t=sent`);
  };
  const discard = () => {
    setConfirmDiscard(false);
    try { localStorage.removeItem(draftKey(wid)); } catch { /* fine */ }
    nav(`${base}/mail`);
  };

  const over = body.length > MAIL_MAX_BODY;
  return (
    <div className="stack">
      <div className="tframe">
        <TitleBar title="New letter" onBack={() => (to.length || subject || body ? setConfirmDiscard(true) : nav(`${base}/mail`))} />
      </div>

      <Card>
        <CardHead title="To" aside={`up to ${MAIL_MAX_RECIPIENTS} players`} />
        <div className="ml-to" onClick={() => toInput.current?.focus()} role="presentation">
          {to.map((n) => (
            <span key={n} className={cx("ml-chip", bad.has(n) && "bad")}>
              <Medallion initial={n} size={30} />
              {n}
              <button type="button" aria-label={`Remove ${n}`} onClick={(e) => { e.stopPropagation(); removeName(n); }}><IconClose /></button>
            </span>
          ))}
          <input
            ref={toInput}
            value={typing}
            placeholder={to.length ? "" : "Player name"}
            aria-label="Add a player"
            autoComplete="off"
            autoCapitalize="none"
            disabled={to.length >= MAIL_MAX_RECIPIENTS}
            onChange={(e) => (/[,;\n]/.test(e.target.value) ? addNames(e.target.value) : setTyping(e.target.value))}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Tab") { if (typing.trim()) { e.preventDefault(); addNames(typing); } } else if (e.key === "Backspace" && !typing && to.length) removeName(to[to.length - 1]); }}
            onBlur={() => typing.trim() && addNames(typing)}
          />
        </div>
        {bad.size > 0 && <p className="hint err" style={{ marginTop: 8 }}>{[...bad].join(", ")} {bad.size === 1 ? "wasn't" : "weren't"} found — names must match exactly.</p>}
        {recent.length > 0 && (
          <div className="ml-recent">
            <span className="lbl">Recent</span>
            {recent.map((n) => <button key={n} type="button" className="chipbtn" onClick={() => addNames(n)} disabled={to.length >= MAIL_MAX_RECIPIENTS}><IconPlus /> {n}</button>)}
          </div>
        )}
      </Card>

      <Card>
        <CardHead title="Subject" />
        <input className="input" value={subject} maxLength={MAIL_MAX_SUBJECT} placeholder="What is this about?" aria-label="Subject" onChange={(e) => setSubject(e.target.value)} />
      </Card>

      <Card className="ml-letterbox">
        <CardHead title="Letter" aside={<span className={cx(over && "crimtxt")}>{fmtNum(body.length)} / {fmtNum(MAIL_MAX_BODY)}</span>} />
        <textarea className="input" value={body} placeholder="Write your letter…" aria-label="Letter" onChange={(e) => setBody(e.target.value)} />
      </Card>

      <StickyBar className="ml-compose-acts">
        <div className="acts">
          <Button kind="steel" size="md" onClick={() => (to.length || subject || body ? setConfirmDiscard(true) : nav(`${base}/mail`))}>Discard</Button>
          <span className="draft">{sending ? "Sending…" : savedAt ? "Draft saved" : ""}</span>
          <Button kind="crimson" size="md" disabled={!canSend} busy={sending} onClick={() => void doSend()}>Send</Button>
        </div>
      </StickyBar>

      <Confirm open={confirmDiscard} onClose={() => setConfirmDiscard(false)} title="Discard this letter?" text="The draft is thrown away." action="Discard" onConfirm={discard} />
    </div>
  );
}
